import React, { useState, useEffect } from 'react';
import {
  CSSReset, Box, Button, Container, FormControl, FormLabel, Input, Select, Text,
  VStack, useColorMode, useToast, ButtonGroup, Spacer, Collapse, Textarea, Spinner, Flex, Progress, useColorModeValue
} from '@chakra-ui/react';
import ReactMarkdown from 'react-markdown';
import { useUser } from '@clerk/clerk-react';
import ReportViewer from './ReportViewer';
import ReportEditor from './ReportEditor';
import ReportLimitReachedDrawer from './DrawerComponent.js';
import Section from './Section';
import ClearableInput from './ClearableInput';

const Walter = ({ ws, isSocketConnected }) => {
  //const [task, setTask] = useState('');
  const [task, setTask] = useState(localStorage.getItem('task') || '');
  const [reportType, setReportType] = useState('research_report');
  const [expandedTask, setExpandedTask] = useState('');
  const [editableExpandedTask, setEditableExpandedTask] = useState('');
  //const [agentOutput, setAgentOutput] = useState('');
  //const [researchReport, setResearchReport] = useState('');
  const [agentOutput, setAgentOutput] = useState(localStorage.getItem('agentOutput') || '');
  const [researchReport, setResearchReport] = useState(localStorage.getItem('researchReport') || '');
  const { colorMode, toggleColorMode } = useColorMode();
  const [isLoading, setIsLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [showExpandedQuery, setShowExpandedQuery] = useState(false);
  const [isViewerEditing, setIsViewerEditing] = useState(false);
  const [sections, setSections] = useState([]);
  const toast = useToast();
  const { user } = useUser();
  const [agentName, setAgentName] = useState(''); // Initialize the agent name state
  const [agentRole, setAgentRole] = useState(''); // Initialize the agent name state
  const [finalAgentOutput, setFinalAgentOutput] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [isManualEditing, setIsManualEditing] = useState(false);
  const [isSectionUpdating, setIsSectionUpdating] = useState(false);
  const [updatingSectionId, setUpdatingSectionId] = useState(null);
  const [availableVersions, setAvailableVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(localStorage.getItem('selectedVersion') || null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  
  const [reportId, setReportId] = useState(null);
  const [isAnySectionEditing, setIsAnySectionEditing] = useState(false);

  const wsUrl = process.env.REACT_APP_WS_URL;
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  //const [socket, setSocket] = useState(null);
  //const [isSocketConnected, setIsSocketConnected] = useState(false);

  useEffect(() => {
    // Update local storage when `task` changes
    localStorage.setItem('task', task);
  }, [task]);

  useEffect(() => {
    // Update local storage when `researchReport` changes
    localStorage.setItem('researchReport', researchReport);
  }, [researchReport]);

  useEffect(() => {
    // Update local storage when `agentOutput` changes
    localStorage.setItem('agentOutput', agentOutput);
  }, [agentOutput]);

  // Save user_id to local storage when user changes
  useEffect(() => {
    if (user && user.id) {
      localStorage.setItem('userId', user.id);
    }
  }, [user]);

  // Update local storage when `sessionId` changes
  useEffect(() => {
    const storedSessionId = localStorage.getItem('sessionId');
    if (storedSessionId) {
        setSessionId(storedSessionId);
    }
}, []);

  useEffect(() => {
    const savedVersions = localStorage.getItem('availableVersions');
    if (savedVersions) {
      setAvailableVersions(JSON.parse(savedVersions));
    }
  }, []);

  useEffect(() => {
    if (!ws) {
      console.log('WebSocket is not initialized.');
      return;
    }

    const handleMessage = (event) => {
      console.log('Received message from WebSocket:', event.data);
      const data = JSON.parse(event.data);
      console.log('Parsed data type:', data.type);
      switch (data.type) {
        case 'logs':
          console.log('Handling logs:', data.output);
          setAgentOutput(prevOutput => `${prevOutput}\n${data.output}`);
          break;
        case 'agent_name':
          console.log('Setting agent name:', data.output);
          setAgentName(data.output);
          break;
        case 'agent_role':
          console.log('Setting agent role:', data.output);
          setAgentRole(data.output);
          break;
        case 'session_id':
          if (data.output) {
            console.log('Received session ID:', data.output);
            setSessionId(data.output);
            console.log('Session ID set and stored:', data.output);
            localStorage.setItem('sessionId', data.output);
          } else {
            console.log('Session ID data output is empty.');
          }
          break;
        case 'report':
          const updatedReport = `${researchReport}\n\n${data.output}`;
          setResearchReport(updatedReport);
          setSections(parseMarkdownToSections(updatedReport));
          setIsLoading(false);
          setIsAnySectionEditing(false); // Reset isAnySectionEditing
          setUpdatingSectionId(null); // Reset updatingSectionId
          
          if (data.report_id) {
            console.log('Report ID1:', data.report_id);
            setReportId(data.report_id);
            fetchAvailableVersions(data.report_id); // Make sure you define this function
          }
          break;
        case 'report_updated':
          setResearchReport(data.updated_content);
          setIsViewerEditing(false);
          setIsSectionUpdating(false);
          setIsLoading(false);
          setUpdatingSectionId(null);
          console.log('Report ID2:', data.report_id);

          // Reset the selected version
          setSelectedVersion(null);

          if (data.report_id) {
            setReportId(data.report_id);
            fetchAvailableVersions(data.report_id); // Make sure you define this function
          }
          toast({
            title: "Success",
            description: `Report ${data.report_id} updated successfully.`,
            status: "success",
            duration: 10000,
            isClosable: true,
          });

          break;
          
          case 'report_saved':
            console.log('Report saved with ID:', data.report_id);
            setReportId(data.report_id);
            setSelectedVersion(null);
            // Optionally, fetch available versions here if needed
            fetchAvailableVersions(data.report_id);
            toast({
              title: "Success",
              description: `Report ${data.report_id} saved successfully.`,
              status: "success",
              duration: 10000,
              isClosable: true,
            });
            break;

        case 'expanded_query':
          setExpandedTask(data.output);
          setEditableExpandedTask(data.output);
          setShowExpandedQuery(true);
          setIsLoading(false);
          break;
        case 'section_updated':
          const updatedSections = sections.map(section => section.id === data.sectionId ? { ...section, content: data.updatedContent } : section);
          setSections(updatedSections);
          break;
        case 'error':  // Handle error messages from the server
          toast({
            title: "Error",
            description: data.message || "An unexpected error occurred.",
            status: "error",
            duration: 10000,
            isClosable: true,
          });
          // You might also want to reset loading states or other flags here
          setIsLoading(false);
          setIsSectionUpdating(false);
          break;

          case 'manual_edit_saved':
            console.log('Manual edit saved with ID:', data.report_id);
            setReportId(data.report_id);
            setSelectedVersion(null); // Reset the selected version if any
    
            // Fetch available versions for the new report_id
            fetchAvailableVersions(data.report_id);
            toast({
              title: "Success",
              description: `Report ${data.report_id} updated successfully.`,
              status: "success",
              duration: 10000,
              isClosable: true,
            });

            break;
          case 'report_limit_reached':
            setIsDrawerOpen(true);
            setIsLoading(false);
            break;
        
        default:
          console.error('Unknown message type:', data.type);
      }
    };

    ws.addEventListener('message', handleMessage);

    return () => {
      ws.removeEventListener('message', handleMessage);
    };
  }, [ws, researchReport, sections]);

  useEffect(() => {
    // Only parse sections if the viewer is in editing mode or about to enter editing mode
    if (isViewerEditing || showExpandedQuery) {
      const parsedSections = parseMarkdownToSections(researchReport);
      setSections(parsedSections);
    }
  }, [researchReport, isViewerEditing, showExpandedQuery]);
  

  const fetchAvailableVersions = async (report_id) => {
    if (report_id) {
      try {
        console.log('Fetching available versions for report:', report_id);
        const response = await fetch(`${backendUrl}/versions/${user.id}/${report_id}`);
        const data = await response.json();
        console.log('Available versions:', data.versions);
        setAvailableVersions(data.versions || []);
        localStorage.setItem('availableVersions', JSON.stringify(data.versions || []));
        setSelectedVersion(data.versions?.[0]?.version);
      } catch (error) {
        console.error('Failed to fetch report versions:', error);
        setAvailableVersions([]);
      }
    }
  };

  // Function to fetch and display the selected version content
  const displaySelectedVersion = async (report_id, selectedTimestamp) => {
    try {
      // Construct the Markdown file path based on your file naming convention
      const markdownFilePath = `${report_id}_${selectedTimestamp}_report.md`;
      const response = await fetch(`${backendUrl}/report-content/${user.id}/${markdownFilePath}`);
      const data = await response.json();
      setResearchReport(data.content); // Assuming the API returns the content of the Markdown file
    } 
  catch (error) {
      console.error('Error fetching selected version:', error);
      toast({
        title: "Error",
        description: "Failed to load the selected version.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const parseMarkdownToSections = (markdown) => {
    const sections = [];
    const lines = markdown.split('\n');
  
    let currentSection = { id: null, content: '' };
    lines.forEach((line, index) => {
      if (line.startsWith('## ') || line.startsWith('### ')) {
        if (currentSection.id !== null) {
          // Push the previous section
          sections.push({ ...currentSection });
          // Reset current section
          currentSection.content = '';
        }
        currentSection.id = sections.length;
      }
      // Add line to current section content
      currentSection.content += line + '\n';
  
      if (index === lines.length - 1) {
        sections.push({ ...currentSection });
      }
    });
  
    return sections;
  };

  const closeDrawer = () => {
    setIsDrawerOpen(false);
  };

  const handleSectionUpdate = async (id, updatedContent, newTask) => {
    setUpdatingSectionId(id);
    //console.log('Updating Section:', id);
    // Check if the WebSocket is connected (OPEN state)
    if (ws && ws.readyState === WebSocket.OPEN) {
      setIsSectionUpdating(true);
      const updateMessage = `update_section ${JSON.stringify({
        sectionId: id,
        updatedContent,
        newTask,
        originalTask: task,
        context: researchReport,
        userID: user.id,
        agentName,
        agentRole
      })}`;
      ws.send(updateMessage);
    } else {
      setUpdatingSectionId(null);
      console.error('WebSocket is not connected. Cannot send update_section message.');
    }
  };

  const handleSectionEditSave = async (id, updatedContent, newTask) => {
    const updatedSections = sections.map(section =>
      section.id === id ? { ...section, content: updatedContent } : section
    );
    setSections(updatedSections); // Update the sections state locally first
  
    // Check if the WebSocket is connected (OPEN state)
    if (ws && ws.readyState === WebSocket.OPEN) {
      const updateSectionMessage = JSON.stringify({
        type: "update_section",
        payload: {
          sectionId: id,
          updatedContent,
          newTask,
          originalTask: task,
          context: researchReport,
          userID: user.id,
          agentName,
          agentRole,
        }
      });
  
      // Sending the WebSocket message
      ws.send(updateSectionMessage);
    } else {
      console.error('WebSocket is not connected. Cannot send update_section message.');
    }
  };

  const handleSaveReport = async () => {
// Assuming `researchReport` holds the latest entire report content after any section updates
    const reportContent = researchReport;
  
    try {
      const response = await fetch(`${backendUrl}/save-report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
// Ensure you're sending the entire report content. Add any other necessary data like userID.
        body: JSON.stringify({ report: reportContent, userID: user.id, sessionID: sessionId }), 
      });
  
      if (!response.ok) throw new Error('Network response was not ok.');
      console.log('Report saved successfully');

      await fetchAvailableVersions(reportId);
      if (availableVersions.length > 0) {
        const latestVersion = availableVersions[availableVersions.length].timestamp; // or use another criteria to identify the newest version
        setSelectedVersion(latestVersion);
      }

    } catch (error) {
      console.error('Failed to save the report:', error);
    }
  };

const handleRequestExpandQuery = (e) => {
e.preventDefault();
    setIsLoading(true);
    
  setAgentOutput('');
  setResearchReport('');
  setAvailableVersions([]);


  if (ws && ws.readyState === WebSocket.OPEN) {  // Use 'ws' passed as props
    const message = `expand_query ${JSON.stringify({ task, userID: user.id })}`;
    ws.send(message);  // Send the message using 'ws'
  } else {
      setIsLoading(false);
toast({
      title: 'Error',
      description: 'WebSocket is not connected. Cannot request expanded query.',
      status: 'error',
      duration: 5000,
      isClosable: true,
    });
    }
  };

  const handleExpandQueryEdit = (e) => {
    setEditableExpandedTask(e.target.value);
  };

  const handleSaveAndSubmitTask = () => {
    setIsLoading(true);
    setAgentOutput('');
    setResearchReport('');

    const finalTask = editableExpandedTask || task;

    if (ws && ws.readyState === WebSocket.OPEN) {  // Use 'ws' passed as props
      const message = `start ${JSON.stringify({ task: finalTask, report_type: reportType, agent: 'Auto Agent', userID: user.id })}`;
      ws.send(message);  // Send the message using 'ws'
      setShowExpandedQuery(false);
    } else {
      setIsLoading(false);
      toast({
        title: 'Error',
        description: 'WebSocket is not connected. Cannot send message.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const enqueueReportGenerationTask = () => {
    setIsLoading(true);
    setAgentOutput('');
    setResearchReport('');

      console.log("inside enqueueReportGenerationTask")
    const finalTask = editableExpandedTask || task;

    if (ws && ws.readyState === WebSocket.OPEN) {
      const message = `enqueue_report_generation ${JSON.stringify({ task: finalTask, report_type: reportType, userID: user.id })}`;
      ws.send(message);
      setShowExpandedQuery(false);
    } else {
      setIsLoading(false);
      toast({
        title: 'Error',
        description: 'WebSocket is not connected. Cannot enqueue report generation task.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };


  const toggleViewerEdit = () => {
    setIsViewerEditing(!isViewerEditing);
    if (isViewerEditing) setIsManualEditing(false);
  };

  // Handle saving the edited content
  const handleViewerEditSave = (editedContent) => {
    setResearchReport(editedContent); // Update the report content state
    setIsViewerEditing(false); // Exit edit mode
  };

  const handleAddSectionBelow = (id) => {
  const newSection = { id: sections.length, content: 'New section content here...' };
  const updatedSections = [...sections];
  updatedSections.splice(id + 1, 0, newSection); // Insert the new section below the current one
  setSections(updatedSections);
};

  const handleManualEdit = () => {
    setIsManualEditing(true); // Enter manual edit mode
    setIsViewerEditing(false); // Ensure structured edit mode is turned off
  };

  const handleManualEditSave = (updatedContent) => {
    setResearchReport(updatedContent); // Update the report with the manually edited content
    setIsManualEditing(false); // Exit manual edit mode
// Construct a message similar to other cases
    console.log("user", user.id)
    const manualEditSaveMessage = `manual_edit_save ${JSON.stringify({
      reportContent: updatedContent,
      userID: user.id,

    })}`;

    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(manualEditSaveMessage);
    } else {
      console.error('WebSocket is not connected. Cannot send manual_edit_save message.');
    }
    
  };

  const handleManualEditCancel = () => {
    setIsManualEditing(false); // Exit manual edit mode
    // Optionally, restore the original content if you have it stored
    // setResearchReport(originalContent);
};

const handleClearAgentOutput = () => {
  setAgentOutput('');
  // Perform any additional cleanup operations if needed
};

const handleClearResearchReport = () => {
  setResearchReport('');
  // Perform any additional cleanup operations if needed
};

const handleClearVersionDropdown = () => {
  setAvailableVersions([]);
  // Perform any additional cleanup operations if needed
};

  const expandedTaskUI = showExpandedQuery && (
    <Collapse in={showExpandedQuery} animateOpacity>
      <Box p={4} borderWidth="1px" borderRadius="md" my={4}>
        <FormLabel>Expanded Task (Editable)</FormLabel>
        <Textarea
          value={editableExpandedTask}
          onChange={handleExpandQueryEdit}
          placeholder="This is your expanded query. You can edit it if needed."
          size="md"
          height="150px"
        />
        <Button mt={4} colorScheme="blue" onClick={enqueueReportGenerationTask} isDisabled={isSectionUpdating} >Begin Research</Button>
        {isLoading && (
            <Progress size="xs" isIndeterminate colorScheme="blue" width="100%" mt={2} />
        )}
      </Box>
    </Collapse>
  );

  return (
    <>
      <CSSReset />
      <Container className="walterContainer" centerContent maxW="container.lg" p={8} backgroundColor={colorMode === 'dark' ? 'gray.800' : 'gray.100'} borderRadius="lg" boxShadow="xl" overflow="auto">
        <VStack spacing={4} align="stretch">
          <Box d="flex" justifyContent="space-between" alignItems="center" width="100%">
            <Text fontSize="3xl" fontWeight="bold" color={colorMode === 'dark' ? 'teal.300' : 'teal.500'}>Welcome to Walter</Text>
          </Box>
          <Box>
            <FormControl isRequired mb={4}>
              <FormLabel>What would you like me to research next?</FormLabel>
              <ClearableInput
                value={task}
                onChange={setTask}
                placeholder="Enter your research task"
                onClearAgentOutput={handleClearAgentOutput}
                onClearResearchReport={handleClearResearchReport}
                onClearVersionDropdown={handleClearVersionDropdown}
              />
            </FormControl>
            {!isLoading ? (
              <Button colorScheme="teal" onClick={handleRequestExpandQuery} isDisabled={isSectionUpdating}>Enhance Prompt</Button>
            ) : (
              <Progress size="xs" isIndeterminate colorScheme="blue" width="100%" mt={2} />
            )}
          </Box>

          {expandedTaskUI}

          {availableVersions.length > 0 && (
            <Flex alignItems="center" mb={4}>
              <Text mr={4}>Report Version:</Text>
              <Select value={selectedVersion || ''} onChange={(e) => setSelectedVersion(e.target.value)} placeholder="Select version">
                {availableVersions.map(version => (
                  <option key={version.version} value={version.timestamp}>
                    Version {version.version} - {version.timestamp}
                  </option>
                ))}
              </Select>
{/* Ensure displaySelectedVersion is only called when this button is clicked */}
              <Button ml={4} onClick={() => reportId && selectedVersion && displaySelectedVersion(reportId, selectedVersion)} colorScheme="blue">Load Version</Button>
            </Flex>
          )}

          <Box bg={colorMode === 'dark' ? 'gray.700' : 'gray.200'} p={4} borderRadius="md" boxShadow="base" overflowY="auto" height="200px" maxWidth= "1000px" mb={4}>
            <Text fontSize="md" mb={2} fontWeight="semibold" color={colorMode === 'dark' ? 'teal.200' : 'teal.500'}>Agent Output:</Text>
            <ReactMarkdown>{agentOutput || 'Awaiting agent activity...'}</ReactMarkdown>
          </Box>

          {
  isManualEditing ? (
// Show ReportViewer for manual editing with editing enabled
   
    <ReportViewer reportContent={researchReport} isEditing={true} onEditSave={handleManualEditSave} onCancelEdit={handleManualEditCancel}/>
  ) : (
    isViewerEditing ? (
      // Structured Edit Mode: Display sections for structured editing
      sections.map((section, index) => (
        <Section
        key={index}
        id={section.id}
        content={section.content}
        ws={ws}
        task={task}
        researchReport={researchReport}
        user={user}
        agentName={agentName}
        agentRole={agentRole}
        onUpdate={handleSectionUpdate}
        isSectionUpdating={isSectionUpdating && updatingSectionId === section.id} // Indicate if this specific section is being updated
        isBeingUpdated={updatingSectionId === section.id} // Indicate if this specific section is being updated
        onEdit={() => {
          setIsAnySectionEditing(true);
          setUpdatingSectionId(section.id); // Update the updatingSectionId to this section's id
        }}
        onCancelUpdate={() => {
          setIsAnySectionEditing(false);
          setUpdatingSectionId(null); // Reset updatingSectionId to null
        }}
        isEditDisabled={isAnySectionEditing && updatingSectionId !== section.id} // Disable edit button for all other sections when one is being edited
      /> 
      ))
    ) : (
      // Viewing Mode: Display the entire report without editing
      <ReportViewer reportContent={researchReport} />
    )
  )
}

<Box d="flex" justifyContent="space-between">
  {!isManualEditing && (
    <Button colorScheme="blue" onClick={toggleViewerEdit} mr={2} isDisabled={isLoading || isSectionUpdating}>
      {isViewerEditing ? 'Cancel Structured Edit' : 'Structured Edit'}
    </Button>
  )}
{/*
  <Button colorScheme="green" onClick={handleSaveReport} mr={2} isDisabled={isLoading || isSectionUpdating}>
    Save Report
  </Button>
*/}
  {!isViewerEditing && (
    <Button colorScheme="orange" onClick={handleManualEdit} isDisabled={isLoading || isSectionUpdating}  >
      Manual Edit
    </Button>
  )}
</Box>
          </VStack>
      </Container>
      <ReportLimitReachedDrawer isOpen={isDrawerOpen} onClose={closeDrawer} />
    </>
  );
};

export default Walter;